import { Component, DestroyRef, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatButtonModule } from "@angular/material/button";
import { MAT_DIALOG_DATA, MatDialogModule } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { ObservableInput, from, of } from "rxjs";
import { MatButtonLoadingDirective } from "../button/button-loading.directive";
import { SaveErrorComponent } from "../save-cancel-buttons/save-error.component";
import { BaseDialog } from "./base-dialog";

export interface ConfirmationDialogData<T = void> {
    message: string;
    onConfirming?: () => ObservableInput<T>;
}

@Component({
    template: `
        <mat-dialog-content>
            {{ data.message }}
            @if (error) {
                <app-save-error [saveError]="error" />
            }
        </mat-dialog-content>
        <mat-dialog-actions>
            <button
                mat-raised-button
                color="primary"
                data-testid="confirm-yes"
                [loading]="loading"
                (click)="confirm()"
            >
                <mat-icon>check</mat-icon>
                Yes
            </button>
            <button
                mat-button
                data-testid="confirm-no"
                [disabled]="loading"
                (click)="cancel()"
            >
                <mat-icon>close</mat-icon>
                No
            </button>
        </mat-dialog-actions>
    `,
    imports: [
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        MatButtonLoadingDirective,
        SaveErrorComponent,
    ],
})
export class ConfirmationDialogComponent<T> extends BaseDialog<ConfirmationDialogData<T>, T> {
    private destroyRef = inject(DestroyRef);

    public data = inject<ConfirmationDialogData<T>>(MAT_DIALOG_DATA);

    public readonly dialogName = "Confirmation";

    public loading = false;
    public error?: Error;

    public constructor() {
        super();

        // TODO Only do this when the callback is in progress
        this.dialogRef.disableClose = true;
    }

    public confirm() {
        this.loading = true;
        this.error = undefined;

        const callbackResult = this.data.onConfirming?.() ?? of(undefined);
        from(callbackResult)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (data) => {
                    this.loading = false;
                    this.resolve(data as T);
                },
                error: (e) => {
                    console.log(e);
                    this.error = e;
                    this.loading = false;
                },
            });
    }
}
